import type { FormEvent } from 'react';
import { Controller } from 'react-hook-form';
import { Button, Checkbox, DatePicker, Input, Label } from '@shared/components';
import { InvoiceRejectionList } from './InvoiceRejectionList';
import type { InvoiceDetailsForm } from '../hooks/useInvoiceDetailsForm';

interface Props {
    form: InvoiceDetailsForm;
    reasons?: string[];
    disabled: boolean;
    disabledReason: string | null;
    submitVariant?: 'primary' | 'outline';
}

const DISABLED_REASON_ID = 'invoice-submit-disabled-reason';
const MILEAGE_WARNING_ID = 'invoice-mileage-warning';
const MILEAGE_CONFIRMATION_LABEL = 'I confirm this mileage is correct';

export function InvoiceSubmitForm({
    form,
    reasons = [],
    disabled,
    disabledReason,
    submitVariant = 'primary',
}: Props) {
    const { control, submit, isSubmitting, mileageWarning } = form;

    const submitDisabled = disabled || isSubmitting || form.mileageBlocked;

    const guardedSubmit = (event: FormEvent<HTMLFormElement>) => {
        if (submitDisabled) {
            event.preventDefault();
            return;
        }
        void submit(event);
    };

    return (
        <form onSubmit={guardedSubmit} className="space-y-4" noValidate>
            <div>
                <Label htmlFor="invoice-number" required>
                    Invoice number
                </Label>
                <Controller
                    name="invoiceNumber"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <Input
                            id="invoice-number"
                            type="text"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={!!error}
                            hint={error?.message}
                            hintId="invoice-number-error"
                            aria-invalid={!!error}
                            aria-describedby={error ? 'invoice-number-error' : undefined}
                        />
                    )}
                />
            </div>

            <div>
                <Label htmlFor="invoiced-at" required>
                    Invoiced date
                </Label>
                <Controller
                    name="invoicedAt"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <DatePicker
                            id="invoiced-at"
                            value={field.value}
                            onChange={field.onChange}
                            maxDate="today"
                            error={!!error}
                            hint={error?.message}
                        />
                    )}
                />
            </div>

            <div>
                <Label htmlFor="invoice-mileage" required>
                    Mileage
                </Label>
                <Controller
                    name="mileage"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <Input
                            id="invoice-mileage"
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={!!error}
                            hint={error?.message}
                            hintId="invoice-mileage-error"
                            aria-invalid={!!error}
                            aria-describedby={
                                [error && 'invoice-mileage-error', mileageWarning && MILEAGE_WARNING_ID]
                                    .filter(Boolean)
                                    .join(' ') || undefined
                            }
                        />
                    )}
                />
                {mileageWarning && (
                    <div className="mt-2 space-y-2">
                        <p id={MILEAGE_WARNING_ID} role="status" className="text-sm text-mp-warning">
                            {mileageWarning}
                        </p>
                        <Controller
                            name="mileageConfirmed"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <>
                                    <Checkbox
                                        id="invoice-mileage-confirmed"
                                        label={MILEAGE_CONFIRMATION_LABEL}
                                        ariaLabel={MILEAGE_CONFIRMATION_LABEL}
                                        checked={field.value ?? false}
                                        onChange={field.onChange}
                                        ariaDescribedBy={MILEAGE_WARNING_ID}
                                    />
                                    {error && (
                                        <p role="alert" className="mt-1 text-xs text-mp-error">
                                            {error.message}
                                        </p>
                                    )}
                                </>
                            )}
                        />
                    </div>
                )}
            </div>

            <InvoiceRejectionList messages={reasons} />

            <div>
                <Button
                    type="submit"
                    size="sm"
                    variant={submitVariant}
                    className="w-full"
                    ariaDisabled={submitDisabled}
                    ariaDescribedBy={disabledReason ? DISABLED_REASON_ID : undefined}
                >
                    Submit invoice
                </Button>
                {disabledReason && (
                    <p id={DISABLED_REASON_ID} className="mt-1.5 text-xs text-mp-muted">
                        {disabledReason}
                    </p>
                )}
            </div>
        </form>
    );
}
